import { getHomePaths, useWorkspaceId } from "@sg-protect/shell";
import { useMemo } from "react";

/**
 * @deprecated
 * This object is deprecated. We only keep it for a couple of weeks, until people have time to create new favorites.
 * We'll delete it on July 1st along with the redirect routes
 */
export const msalPaths = {
    home: "/app/home",

    // Overview
    overview: "/app/overview",

    // Insights
    insights: "/app/insights",
    insightsDetail: "/app/insights/:insightId",
    insightsSummary: "/app/insights/:insightId/summary",
    insightsObjectList: "/app/insights/:insightId/object-list",
    insightsPolicies: "/app/insights/:insightId/policies",

    // Tenant Info
    tenantInfo: "/app/tenant-info",

    // Tenant Assessment
    tenantAssessment: "/app/tenant-assessment",
    tenantAssessmentMissingLicense: "/app/tenant-assessment/missing-sharegate-license",
    tenantAssessmentNoAccess: "/app/tenant-assessment/no-sg-workspace-protect-access",

    tenantAssessmentConsented: "/app/tenant-assessment/consented",

    // Reports
    reports: "/app/reports",
    reportsView: "/app/reports/view",
    reportsCreate: "/app/reports/create",
    reportsGenerate: "/app/reports/ai-generate",
    reportsUpdate: "/app/reports/update/:reportId",
    permissionsMatrix: "/app/reports/permissions-matrix",
    permissionsMatrixSiteSelection: "/app/reports/permissions-matrix/site-selection",
    permissionsMatrixReport: "/app/reports/permissions-matrix/report",
    userAccess: "/app/reports/user-access",
    userAccessSelection: "/app/reports/user-access/selection",
    userAccessReport: "/app/reports/user-access/report",
    groupAccess: "/app/reports/group-access",
    groupAccessSelection: "/app/reports/group-access/selection",
    groupAccessReport: "/app/reports/group-access/report",
    versionStorageReport: "/app/reports/version-storage",

    // Automations
    automations: "/app/policies",
    automationsCreate: "/app/policies/create",
    automationsUpdate: "/app/policies/update/:automationId",

    // Reviews
    reviews: "/app/reviews",
    reviewsDetail: "/app/reviews/:reviewId",
    reviewsUpdate: "/app/reviews/update/:reviewId",

    // Setup routes
    setup: "/app/tenant-assessment/setup",
    setupDataRegion: "/app/tenant-assessment/setup/data-region",
    setupSourceTenant: "/app/tenant-assessment/setup/source-tenant",
    setupReviewPermissions: "/app/tenant-assessment/setup/review-permissions",
    setupConnected: "/app/tenant-assessment/setup/connected",
    setupRequestReviewPermission: "/app/tenant-assessment/setup/request/review-permissions",
    setupRequestConnected: "/app/tenant-assessment/setup/request/connected",

    // Others
    workspaceTenantReconsentRequest: "/app/workspace-tenant-reconsent-request",
    remediationConsent: "/app/remediation-consent",
    remediationPostConsent: "/app/post-consent",
    sensitivityLabels: "/app/sensitivity-labels",
    activityLog: "/app/activity-log"
} as const;

export const workspacePaths = {
    home: "/app/:workspaceId/home",

    // Overview
    overview: "/app/:workspaceId/overview",

    // Insights
    insights: "/app/:workspaceId/insights",
    insightsDetail: "/app/:workspaceId/insights/:insightId",
    insightsSummary: "/app/:workspaceId/insights/:insightId/summary",
    insightsObjectList: "/app/:workspaceId/insights/:insightId/object-list",
    insightsPolicies: "/app/:workspaceId/insights/:insightId/policies",

    // Tenant Info
    tenantInfo: "/app/:workspaceId/tenant-info",

    // Recommendations
    recommendations: "/app/:workspaceId/recommendations",

    // Tenant Assessment
    tenantAssessment: "/app/:workspaceId/tenant-assessment",
    tenantAssessmentMissingLicense: "/app/:workspaceId/tenant-assessment/missing-sharegate-license",
    tenantAssessmentNoAccess: "/app/:workspaceId/tenant-assessment/no-sg-workspace-protect-access",

    tenantAssessmentConsented: "/app/:workspaceId/tenant-assessment/consented",

    // Reports
    reports: "/app/:workspaceId/reports",
    reportsView: "/app/:workspaceId/reports/view",
    reportsCreate: "/app/:workspaceId/reports/create",
    reportsGenerate: "/app/:workspaceId/reports/ai-generate",
    reportsUpdate: "/app/:workspaceId/reports/update/:reportId",
    permissionsMatrix: "/app/:workspaceId/reports/permissions-matrix",
    permissionsMatrixSiteSelection: "/app/:workspaceId/reports/permissions-matrix/site-selection",
    permissionsMatrixReport: "/app/:workspaceId/reports/permissions-matrix/report",
    userAccess: "/app/:workspaceId/reports/user-access",
    userAccessSelection: "/app/:workspaceId/reports/user-access/selection",
    userAccessReport: "/app/:workspaceId/reports/user-access/report",
    groupAccess: "/app/:workspaceId/reports/group-access",
    groupAccessSelection: "/app/:workspaceId/reports/group-access/selection",
    groupAccessReport: "/app/:workspaceId/reports/group-access/report",
    versionStorageReport: "/app/:workspaceId/reports/version-storage",

    // Automations
    automations: "/app/:workspaceId/policies",
    automationsCreate: "/app/:workspaceId/policies/create",
    automationsUpdate: "/app/:workspaceId/policies/update/:automationId",

    // Reviews
    reviews: "/app/:workspaceId/reviews",
    reviewsDetail: "/app/:workspaceId/reviews/:reviewId",
    reviewsUpdate: "/app/:workspaceId/reviews/update/:reviewId",

    // Workspace Details
    workspaceDetails: "/app/:workspaceId/workspace-details/:siteCollectionId",
    workspaceDetailsSharingLinks: "/app/:workspaceId/workspace-details/:siteCollectionId/sharing-links",
    workspaceDetailsLibraries: "/app/:workspaceId/workspace-details/:siteCollectionId/libraries",
    workspaceDetailsDetails: "/app/:workspaceId/workspace-details/:siteCollectionId/details",
    workspaceDetailsOverview: "/app/:workspaceId/workspace-details/:siteCollectionId/overview",
    workspaceDetailsMembership: "/app/:workspaceId/workspace-details/:siteCollectionId/membership",
    workspaceDetailsPermissions: "/app/:workspaceId/workspace-details/:siteCollectionId/permissions",
    workspaceDetailsActivityLog: "/app/:workspaceId/workspace-details/:siteCollectionId/activity-log",

    // User Details
    userDetails: "/app/:workspaceId/user-details/:userId",
    userDetailsOverview: "/app/:workspaceId/user-details/:userId/overview",
    userDetailsLicenses: "/app/:workspaceId/user-details/:userId/licenses",
    userDetailsSharingLinks: "/app/:workspaceId/user-details/:userId/sharing-links",
    userDetailsMembership: "/app/:workspaceId/user-details/:userId/membership",
    userDetailsPermissions: "/app/:workspaceId/user-details/:userId/permissions",

    // Setup routes
    setup: "/app/:workspaceId/tenant-assessment/setup",
    setupDataRegion: "/app/:workspaceId/tenant-assessment/setup/data-region",
    setupSourceTenant: "/app/:workspaceId/tenant-assessment/setup/source-tenant",
    setupReviewPermissions: "/app/:workspaceId/tenant-assessment/setup/review-permissions",
    setupConnected: "/app/:workspaceId/tenant-assessment/setup/connected",
    setupRequestReviewPermission: "/app/:workspaceId/tenant-assessment/setup/request/review-permissions",
    setupRequestConnected: "/app/:workspaceId/tenant-assessment/setup/request/connected",

    // Others
    workspaceTenantReconsentRequest: "/app/:workspaceId/workspace-tenant-reconsent-request",
    remediationConsent: "/app/:workspaceId/remediation-consent",
    remediationPostConsent: "/app/post-consent", // this path needs to avoid dynamic segments, since it is used as a callback in Microsoft
    sensitivityLabels: "/app/:workspaceId/sensitivity-labels",
    activityLog: "/app/:workspaceId/activity-log",

    // Restricted-state routes
    homeRestrictedState: getHomePaths().restrictedState,
    tenantInfoRestrictedState: "/app/:workspaceId/tenant-info/restricted",
    insightsRestrictedState: "/app/:workspaceId/insights/restricted",
    reportsRestrictedState: "/app/:workspaceId/reports/restricted",
    automationsRestrictedState: "/app/:workspaceId/policies/restricted",
    reviewsRestrictedState: "/app/:workspaceId/reviews/restricted",
    sensitivityLabelsRestrictedState: "/app/:workspaceId/sensitivity-labels/restricted",
    activityLogRestrictedState: "/app/:workspaceId/activity-log/restricted",
    workspaceDetailsRestrictedState: "/app/:workspaceId/workspace-details/restricted",

    // Zero-state routes
    homeZeroState: getHomePaths().zeroState,
    tenantInfoZeroState: "/app/:workspaceId/tenant-info/get-started",
    insightsZeroState: "/app/:workspaceId/insights/get-started",
    reportsZeroState: "/app/:workspaceId/reports/get-started",
    automationsZeroState: "/app/:workspaceId/policies/get-started",
    reviewsZeroState: "/app/:workspaceId/reviews/get-started",
    activityLogZeroState: "/app/:workspaceId/activity-log/get-started"
} as const;

export const setupConsentErrorCodes = {
    migrateAssessmentTenantIsPrimaryTenant: "migrate-assessment-tenant-is-primary-tenant",
    primaryTenantIsMigrateAssessmentTenant: "primary-tenant-is-migrate-assessment-tenant",
    sameTenant: "same-tenant"
} as const;

export function getPaths(options: { workspaceId: string }): PathsWithWorkspaceId<typeof workspacePaths>;
export function getPaths(options?: { workspaceId?: string }): typeof workspacePaths;
export function getPaths(options?: { workspaceId?: string }) {
    return options?.workspaceId ? prefillPaths(workspacePaths, options.workspaceId) : workspacePaths;
}

type ReplacePathParamKey<
    Path extends string,
    Key extends string,
    Value extends string
> = Path extends `${infer Before}/:${Key}/${infer After}` ? `${Before}/${Value}/${After}` : Path;

type PathsWithWorkspaceId<T extends Record<string, string>> = {
    [K in keyof T]: ReplacePathParamKey<T[K], "workspaceId", string>;
};

function prefillPaths<const T extends Record<string, string>>(paths: T, workspaceId: string): PathsWithWorkspaceId<T> {
    return Object.fromEntries(
        Object.entries(paths).map(([key, value]) => [key, value.replaceAll(":workspaceId", workspaceId)])
    ) as PathsWithWorkspaceId<T>;
}

export function usePaths(options: { prefillWorkspaceId: false }): typeof workspacePaths;
export function usePaths(): PathsWithWorkspaceId<typeof workspacePaths>;
export function usePaths(options?: { prefillWorkspaceId: false }) {
    const workspaceId = useWorkspaceId();

    return useMemo(() => {
        return options?.prefillWorkspaceId === false ? workspacePaths : prefillPaths(workspacePaths, workspaceId);
    }, [options?.prefillWorkspaceId, workspaceId]);
}
